import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { User, UserRole, Session, SessionUser } from "@/types";
import type { ApiLoginResponseData, ApiUserMeResponse } from "@/types/api-auth";
import { apiClient, setAccessToken, setRefreshToken, setOnTokenRefreshed } from "@/lib/api-client";
import {
  refreshAccessToken,
  setCoordinatorSkipAutoRedirect,
  startAuthRefreshCoordinator,
  stopAuthRefreshCoordinator,
} from "@/lib/auth-refresh-coordinator";
import {
  recordTokenExpiry,
  recordTokenExpiryFromAccessToken,
} from "@/lib/auth-session-expiry";
import {
  fetchUsersMeWithRetry,
  SESSION_REVALIDATION_INTERVAL_MS,
  shouldClearStoredSessionOnRestoreFailure,
  userFromSessionSnapshot,
} from "@/lib/auth-session-restore";
import { mapLoginResponseToUser } from "@/lib/api-mappers";
import { buildSessionUser, fetchStudiosFromApi } from "@/lib/auth-studios-sync";
import type { GoogleAuthResult } from "@/lib/google-oauth";
import i18n from "@/lib/i18n";
import { isSupportedUiLocale, persistUiLocale } from "@/lib/i18n/constants";
import { WS_SESSION_EVENT, SESSION_TERMINATED_EVENT } from "@/lib/ws-events";
import {
  SESSION_STORAGE_KEY,
  clearAuthStorage,
  readStoredSession,
} from "@/lib/auth-storage";

interface AuthContextType {
  user: User | null;
  session: Session;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginWithGoogle: (result: GoogleAuthResult) => void;
  register: (studioName: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  getRoleForStudio: (studioId: string) => UserRole;
  refreshSession: () => Promise<void>;
  loginAttempts: number;
  lockoutUntil: number | null;
  handleLoginFailure: () => void;
  /** Set after GET /studios; used to sync initial studio context */
  serverCurrentStudioId: string | "all" | null;
  /** Replace user.studios with fresh list from GET /studios (e.g. after create studio) */
  refreshStudios: () => Promise<void>;
  /** Refetch current user and studios (e.g. after completing onboarding) */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const LOCKOUT_THRESHOLD = 10;
const LOCKOUT_DURATION_MS = 30 * 60 * 1000;

function getStoredSession(): { accessToken: string; refreshToken: string | null; user: SessionUser } | null {
  return readStoredSession<{ accessToken: string; refreshToken: string | null; user: SessionUser }>();
}

function applyAccessTokenExpiry(accessToken: string, expiresIn?: number): void {
  if (typeof expiresIn === "number" && expiresIn > 0) {
    recordTokenExpiry(expiresIn);
  } else {
    recordTokenExpiryFromAccessToken(accessToken);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session>({ accessToken: null, refreshToken: null, user: null });
  const [isLoading, setIsLoading] = useState(true);
  const [loginAttempts, setLoginAttempts] = useState(() => {
    return parseInt(localStorage.getItem("oktogo_login_attempts") || "0", 10);
  });
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(() => {
    const stored = localStorage.getItem("oktogo_lockout_until");
    if (!stored) return null;
    const val = parseInt(stored, 10);
    return val > Date.now() ? val : null;
  });
  const [serverCurrentStudioId, setServerCurrentStudioId] = useState<string | "all" | null>(null);
  const [sessionRevalidationPending, setSessionRevalidationPending] = useState(false);

  const syncStudiosFromApi = useCallback(async () => {
    const synced = await fetchStudiosFromApi();
    if (!synced) return;
    setUser((prev) => (prev ? { ...prev, studios: synced.studios, studioRoles: synced.studioRoles } : null));
    setServerCurrentStudioId(synced.currentStudioId);
  }, []);

  const applyPreferredUiLocale = useCallback((preferredUiLocale: string | null | undefined) => {
    if (!preferredUiLocale || !isSupportedUiLocale(preferredUiLocale)) return;
    persistUiLocale(preferredUiLocale);
    if (i18n.language !== preferredUiLocale) {
      void i18n.changeLanguage(preferredUiLocale);
    }
  }, []);

  const updateStoredTokens = useCallback((newAccessToken: string, newRefreshToken: string | null) => {
    const storage = localStorage.getItem(SESSION_STORAGE_KEY) ? localStorage : sessionStorage;
    const stored = getStoredSession();
    if (stored) {
      storage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
        ...stored,
        accessToken: newAccessToken,
        refreshToken: newRefreshToken || stored.refreshToken,
      }));
    }
    setSession((prev) => ({
      ...prev,
      accessToken: newAccessToken,
      refreshToken: newRefreshToken || prev.refreshToken,
    }));
  }, []);

  useEffect(() => {
    setOnTokenRefreshed(updateStoredTokens);
    return () => setOnTokenRefreshed(null);
  }, [updateStoredTokens]);

  useEffect(() => {
    setCoordinatorSkipAutoRedirect(isLoading);
    if (isLoading || !user) {
      if (!user) stopAuthRefreshCoordinator();
      return;
    }
    startAuthRefreshCoordinator();
    return () => stopAuthRefreshCoordinator();
  }, [isLoading, user]);

  const applyValidatedSession = useCallback(
    (
      stored: { accessToken: string; refreshToken: string | null; user: SessionUser },
      meData: ApiUserMeResponse,
    ) => {
      try {
        const mappedUser = mapLoginResponseToUser({
          user: { ...meData, studios: meData.studios || [] },
        });
        applyPreferredUiLocale(mappedUser.preferredUiLocale);
        setUser(mappedUser);
        const currentAccessToken = meData._refreshedAccessToken || stored.accessToken;
        applyAccessTokenExpiry(currentAccessToken);
        setSession({
          accessToken: currentAccessToken,
          refreshToken: stored.refreshToken,
          user: buildSessionUser(mappedUser),
        });
        setSessionRevalidationPending(false);
        void syncStudiosFromApi().catch(() => {});
        return true;
      } catch {
        return false;
      }
    },
    [applyPreferredUiLocale, syncStudiosFromApi],
  );

  const applyDegradedSession = useCallback(
    (stored: { accessToken: string; refreshToken: string | null; user: SessionUser }) => {
      const mappedUser = userFromSessionSnapshot(stored.user);
      applyPreferredUiLocale(mappedUser.preferredUiLocale);
      setUser(mappedUser);
      applyAccessTokenExpiry(stored.accessToken);
      setSession({
        accessToken: stored.accessToken,
        refreshToken: stored.refreshToken,
        user: stored.user,
      });
      setSessionRevalidationPending(true);
    },
    [applyPreferredUiLocale],
  );

  const clearSessionState = useCallback(() => {
    setSessionRevalidationPending(false);
    setAccessToken(null);
    setRefreshToken(null);
    clearAuthStorage();
  }, []);

  useEffect(() => {
    const stored = getStoredSession();
    if (!stored?.accessToken || !stored?.user) {
      setIsLoading(false);
      return;
    }

    setAccessToken(stored.accessToken);
    setRefreshToken(stored.refreshToken || null);

    let cancelled = false;

    void fetchUsersMeWithRetry(() =>
      apiClient<ApiUserMeResponse>("/users/me", { skipAutoRedirect: true }),
    )
      .then((outcome) => {
        if (cancelled) return;

        if (outcome.kind === "ok") {
          if (!applyValidatedSession(stored, outcome.data)) {
            clearSessionState();
          }
          return;
        }

        if (outcome.kind === "auth_failed") {
          clearSessionState();
          return;
        }

        applyDegradedSession(stored);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applyDegradedSession, applyValidatedSession, clearSessionState]);

  useEffect(() => {
    if (!sessionRevalidationPending || !user) return;

    const revalidate = async () => {
      const res = await apiClient<ApiUserMeResponse>("/users/me", { skipAutoRedirect: true });
      if (res.success && res.data) {
        const stored = getStoredSession();
        if (stored?.accessToken && stored.user && applyValidatedSession(stored, res.data)) {
          return;
        }
      }
      if (shouldClearStoredSessionOnRestoreFailure(res)) {
        setUser(null);
        setSession({ accessToken: null, refreshToken: null, user: null });
        clearSessionState();
      }
    };

    const timerId = window.setInterval(() => {
      void revalidate();
    }, SESSION_REVALIDATION_INTERVAL_MS);

    void revalidate();

    return () => {
      window.clearInterval(timerId);
    };
  }, [sessionRevalidationPending, user, applyValidatedSession, clearSessionState]);

  useEffect(() => {
    if (!lockoutUntil) return;
    const remaining = lockoutUntil - Date.now();
    if (remaining <= 0) {
      setLockoutUntil(null);
      setLoginAttempts(0);
      return;
    }
    const timer = setTimeout(() => {
      setLockoutUntil(null);
      setLoginAttempts(0);
    }, remaining);
    return () => clearTimeout(timer);
  }, [lockoutUntil]);

  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    if (lockoutUntil && Date.now() < lockoutUntil) {
      throw new Error("Account locked");
    }
    setIsLoading(true);

    try {
      const res = await apiClient<ApiLoginResponseData>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (!res.success || !res.data) {
        setIsLoading(false);
        throw new Error(res.error?.detail ?? res.error?.message ?? "Login failed");
      }

      const { accessToken, refreshToken, user: userData, expiresIn } = res.data;
      setAccessToken(accessToken);
      setRefreshToken(refreshToken || null);
      applyAccessTokenExpiry(accessToken, expiresIn);

      const mappedUser = mapLoginResponseToUser({ user: userData });
      applyPreferredUiLocale(mappedUser.preferredUiLocale);

      const newSession: Session = {
        accessToken,
        refreshToken,
        user: buildSessionUser(mappedUser),
      };

      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ accessToken, refreshToken, user: newSession.user }));

      setUser(mappedUser);
      setSession(newSession);
      setLoginAttempts(0);
      localStorage.removeItem("oktogo_login_attempts");
      localStorage.removeItem("oktogo_lockout_until");

      void syncStudiosFromApi().catch(() => {});
    } finally {
      setIsLoading(false);
    }
  }, [applyPreferredUiLocale, lockoutUntil, syncStudiosFromApi]);

  const loginWithGoogle = useCallback((result: GoogleAuthResult) => {
    const { user: mappedUser, accessToken, refreshToken, expiresIn } = result;
    setAccessToken(accessToken);
    setRefreshToken(refreshToken || null);
    applyAccessTokenExpiry(accessToken, expiresIn);
    applyPreferredUiLocale(mappedUser.preferredUiLocale);

    const newSession: Session = {
      accessToken,
      refreshToken,
      user: buildSessionUser(mappedUser),
    };

    // Always persist Google sessions to localStorage (no "remember me" prompt)
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ accessToken, refreshToken, user: newSession.user }));

    setUser(mappedUser);
    setSession(newSession);
    setLoginAttempts(0);
    localStorage.removeItem("oktogo_login_attempts");
    localStorage.removeItem("oktogo_lockout_until");

    void syncStudiosFromApi().catch(() => {});
  }, [applyPreferredUiLocale, syncStudiosFromApi]);

  const handleLoginFailure = useCallback(() => {
    const next = loginAttempts + 1;
    setLoginAttempts(next);
    localStorage.setItem("oktogo_login_attempts", String(next));
    if (next >= LOCKOUT_THRESHOLD) {
      const until = Date.now() + LOCKOUT_DURATION_MS;
      setLockoutUntil(until);
      localStorage.setItem("oktogo_lockout_until", String(until));
    }
  }, [loginAttempts]);

  const register = useCallback(async (studioName: string, email: string, password: string) => {
    setIsLoading(true);
    try {
      const res = await apiClient<unknown>("/auth/register", {
        method: "POST",
        body: JSON.stringify({ email, password, studioName }),
      });
      if (!res.success) {
        throw new Error(res.error?.detail ?? res.error?.message ?? "Registration failed");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    stopAuthRefreshCoordinator();
    setSessionRevalidationPending(false);
    const token = session.accessToken;
    const refreshToken = session.refreshToken;
    if (token) {
      const logoutBody = refreshToken ? { refreshToken } : {};
      apiClient<unknown>("/auth/logout", {
        method: "POST",
        body: JSON.stringify(logoutBody),
        skipAutoRedirect: true,
      }).catch(() => {});
    }
    setUser(null);
    setSession({ accessToken: null, refreshToken: null, user: null });
    setAccessToken(null);
    setRefreshToken(null);
    clearAuthStorage();
  }, [session.accessToken, session.refreshToken]);

  const refreshSession = useCallback(async () => {
    await refreshAccessToken({ skipAutoRedirect: true });
  }, []);

  const getRoleForStudio = useCallback(
    (studioId: string): UserRole => {
      if (!user) return "Staff";
      const studioRole = (user.studioRoles ?? []).find((sr) => sr.studioId === studioId);
      return studioRole?.role ?? "Staff";
    },
    [user]
  );

  const refreshStudios = useCallback(async () => {
    if (!user) return;
    await syncStudiosFromApi();
  }, [user, syncStudiosFromApi]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await apiClient<ApiUserMeResponse>("/users/me", { skipAutoRedirect: true });
      if (!res.success || !res.data) return;
      let mappedUser: ReturnType<typeof mapLoginResponseToUser>;
      try {
        mappedUser = mapLoginResponseToUser({ user: { ...res.data, studios: res.data.studios || [] } });
      } catch {
        return;
      }
      applyPreferredUiLocale(mappedUser.preferredUiLocale);
      setUser(mappedUser);
      setSession((prev) => ({
        ...prev,
        user: buildSessionUser(mappedUser),
      }));
      await syncStudiosFromApi();
    } catch {
      // ignore
    }
  }, [applyPreferredUiLocale, syncStudiosFromApi]);

  useEffect(() => {
    if (!user) return;
    const onSessionScopeChanged = () => {
      void refreshStudios();
    };
    window.addEventListener(WS_SESSION_EVENT, onSessionScopeChanged);
    return () => window.removeEventListener(WS_SESSION_EVENT, onSessionScopeChanged);
  }, [user, refreshStudios]);

  useEffect(() => {
    const onSessionTerminated = () => {
      stopAuthRefreshCoordinator();
      setUser(null);
      setSession({ accessToken: null, refreshToken: null, user: null });
      setAccessToken(null);
      setRefreshToken(null);
    };
    window.addEventListener(SESSION_TERMINATED_EVENT, onSessionTerminated);
    return () => window.removeEventListener(SESSION_TERMINATED_EVENT, onSessionTerminated);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        isAuthenticated: !!user,
        login,
        loginWithGoogle,
        register,
        logout,
        getRoleForStudio,
        refreshSession,
        loginAttempts,
        lockoutUntil,
        handleLoginFailure,
        serverCurrentStudioId,
        refreshStudios,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
